import { MUTED, colorFor } from "../components/charts/chartTheme";
import { LOCALES } from "../components/charts/format";

// As chaves que a pagina de analytics conhece, por dimensao: a ordem fixa (a mesma em todos os
// periodos) e a posicao da paleta de cada uma. A cor segue a chave e nunca a contagem (spec do
// RPAA-286, decisao 12). Uma chave que nao esteja aqui, um valor novo que o backend comece a mandar,
// aparece depois das conhecidas, a cinzento e com o nome tal e qual: o conjunto dos `outcome` das
// encomendas, por exemplo, e aberto.
// As posicoes nao seguem a ordem de proposito. Numa barra empilhada uma chave ausente junta duas que
// nao eram vizinhas, e com oito cores ha pares que o validador da dataviz reprova lado a lado (o
// laranja e o ambar, por exemplo). As posicoes foram escolhidas por procura, a 15-Out, para que os
// exemplos do contrato nunca juntem um desses pares e as combinacoes provaveis o facam o menos possivel.
// A voz (posicao 5) vem primeiro na ordem de proposito: a unica vizinha dela numa barra e a `typed` (1),
// par que passa nos dois temas, ao passo que a 5 ao lado da 3 ou da 2 o validador reprova. Sem `typed`
// juntava-se a `quick_reply`, caso raro (quem fala e escolhe sugestoes sem nunca escrever) que se assume.
export const DIMENSIONS = {
  source: {
    order: ["voice", "typed", "quick_reply", "choice", "invite", "host", "launcher", "restore", "docked"],
    slots: { voice: 5, typed: 1, quick_reply: 3, choice: 2, invite: 7, host: 4, launcher: 6, restore: 5, docked: 8 },
  },
  composition: { order: ["only_typed", "mixed", "only_suggestions"], slots: { only_typed: 1, mixed: 3, only_suggestions: 2 } },
  // Os tres estados da sessao iniciada (RPAA-286, item 10) com as posicoes da `composition`, outro conjunto
  // de tres numa barra empilhada: os pares vizinhos (1 e 3, 3 e 2, e 1 e 2 sem o do meio) sao os que ja passam la.
  sign_in: { order: ["at_start", "during", "anonymous"], slots: { at_start: 1, during: 3, anonymous: 2 } },
  via: {
    order: ["inactivity", "customer_header", "customer_agent_button", "operator", "contact_merged", "open"],
    slots: { inactivity: 1, customer_header: 4, customer_agent_button: 5, operator: 7, contact_merged: 8, open: 6 },
  },
  outcome: {
    order: ["success", "no_agent_available", "agents_busy", "no_handoff_target", "error"],
    slots: { success: 5, no_agent_available: 6, agents_busy: 4, no_handoff_target: 1, error: 7 },
  },
  hours: { order: ["in", "out"], slots: { in: 1, out: 2 } },
  // Cada ferramenta de encomendas (Radio Popular, VTEX, MODZ) escreve o seu conjunto de `outcome`, e
  // um chat corre uma so: por isso as posicoes repetem-se entre conjuntos, e so contam os vizinhos
  // dentro de um conjunto. `ownership_mismatch` e `no_strategy` sao raros e ficam a cinzento, e o
  // `no_strategy` vem primeiro para os dois cinzentos da VTEX nunca ficarem lado a lado.
  order_outcome: {
    order: [
      "no_strategy", "found", "found_delivered", "found_in_transit", "found_cancelled", "found_other", "no_orders",
      "not_found", "not_authenticated", "missing_order_id", "ownership_mismatch", "info_returned",
      "asked_for_clarification", "fallback", "error",
    ],
    slots: {
      found: 1, found_delivered: 3, found_in_transit: 4, found_cancelled: 1, found_other: 5, no_orders: 8, not_found: 6,
      not_authenticated: 7, missing_order_id: 8, info_returned: 4, asked_for_clarification: 7, fallback: 3, error: 2,
    },
  },
  matched: { order: ["true", "false"], slots: { true: 1, false: 2 } },
  device: { order: ["mobile", "desktop", "tablet"], slots: { mobile: 1, desktop: 2, tablet: 3 } },
  // Procurado de novo a 15-Out: passa em todos os vizinhos da ordem completa, com o cinzento no fim.
  os: {
    order: ["iOS", "Android", "Windows", "macOS", "iPadOS", "Linux", "ChromeOS"],
    slots: { iOS: 6, Android: 1, Windows: 4, macOS: 3, iPadOS: 8, Linux: 7, ChromeOS: 2 },
  },
  language: { order: ["pt", "en", "es", "fr", "de", "it"], slots: { pt: 1, en: 3, es: 8, fr: 4, de: 7, it: 6 } },
  browser: {
    order: ["Chrome", "Safari", "Edge", "Firefox", "Samsung Internet", "Opera"],
    slots: { Chrome: 1, Safari: 4, Edge: 6, Firefox: 5, "Samsung Internet": 7, Opera: 8 },
  },
  // As faixas sao ordinais e desenham-se em barras de uma cor: nao tem posicoes da paleta.
  bucket: { order: ["0-1m", "1-5m", "5-15m", "15-60m", "60m+", "none"], slots: {} },
  // As estrelas sao ordinais, como as faixas: barras de uma cor.
  rating: { order: ["1", "2", "3", "4", "5"], slots: {} },
};

// O nome de um valor na lingua de quem ve, ou o valor tal e qual. O translate devolve a propria
// chave quando nao a conhece.
export const labelFor = (dimension, value, translate) => {
  const key = `analytics.${dimension}.${value}`;
  const text = translate(key);
  return text === key ? value : text;
};

// Um breakdown do contrato ({total, values, unattributed}) em segmentos de uma barra empilhada: as
// chaves conhecidas pela ordem fixa, as outras depois, e o que falta para o total no fim, a cinzento.
export const toSegments = (breakdown, dimension, translate) => {
  const known = DIMENSIONS[dimension] || { order: [], slots: {} };
  const present = Object.keys(breakdown.values).filter((key) => breakdown.values[key] > 0);
  const ordered = [...known.order.filter((key) => present.includes(key)), ...present.filter((key) => !known.order.includes(key))];
  const segments = ordered.map((key) => ({
    key,
    value: breakdown.values[key],
    label: labelFor(dimension, key, translate),
    color: colorFor(known.slots, key),
  }));
  if (breakdown.unattributed > 0) {
    segments.push({ key: "unattributed", value: breakdown.unattributed, label: translate("analyticsUnattributed"), color: MUTED });
  }
  return segments;
};

// Uma dimensao ordinal (as faixas) em barras: as faixas conhecidas todas e com zeros, depois as que a
// pagina nao conhece, pela ordem em que chegam e com o nome tal e qual (como no toSegments), e o que
// falta para o total no fim, como "sem informacao".
export const toBars = (breakdown, dimension, translate) => {
  const { order } = DIMENSIONS[dimension];
  const unknown = Object.keys(breakdown.values).filter((key) => !order.includes(key));
  const bars = [...order, ...unknown].map((key) => ({ key, label: labelFor(dimension, key, translate), value: breakdown.values[key] || 0 }));
  if (breakdown.unattributed > 0) {
    bars.push({ key: "unattributed", label: translate("analyticsUnattributed"), value: breakdown.unattributed });
  }
  return bars;
};

// Um breakdown em linhas por contagem descendente, para as barras por ordem (spec do redesign, R7). O
// que falta para o total vem no fim e a cinzento. Ordena-se aqui, e nao pela ordem das chaves do JSON,
// porque um objeto de JavaScript poe as chaves que parecem inteiros a frente das outras.
export const rankedRows = (breakdown, dimension, translate) => {
  const rows = Object.entries(breakdown.values)
    .filter(([, value]) => value > 0)
    .sort(([keyA, valueA], [keyB, valueB]) => valueB - valueA || keyA.localeCompare(keyB))
    .map(([key, value]) => ({ key, value, label: labelFor(dimension, key, translate), muted: false }));
  if (breakdown.unattributed > 0) {
    rows.push({ key: "unattributed", value: breakdown.unattributed, label: translate("analyticsUnattributed"), muted: true });
  }
  return rows;
};

// 0 e segunda-feira, como no backend. 2026-10-12 foi uma segunda-feira.
const weekdayName = (index, language, style = "short") =>
  new Intl.DateTimeFormat(LOCALES[language] || language, { weekday: style, timeZone: "UTC" }).format(new Date(Date.UTC(2026, 9, 12 + index)));

export const weekdayLong = (index, language) => weekdayName(index, language, "long");

export const weekdayBars = (values, language) => values.map((value, index) => ({ key: String(index), label: weekdayName(index, language), value }));

export const hourBars = (values) => values.map((value, hour) => ({ key: String(hour), label: `${hour}h`, value }));

// Uma data AAAA-MM-DD com o dia e o mes abreviado, como no eixo diario: "8 Oct" ou "8/10".
const dayFormat = (language) => new Intl.DateTimeFormat(LOCALES[language] || language, { day: "numeric", month: "short", timeZone: "UTC" });
const dayOf = (isoDate) => new Date(`${isoDate}T00:00:00Z`);

export const formatDay = (isoDate, language) => dayFormat(language).format(dayOf(isoDate));

// A hora local que um balde por hora ja traz: 17 em "2026-10-20T17:00:00+01:00". E por ela que o rotulo se
// le ("17h") e que ontem se alinha com hoje (`alignPrevious`).
export const localHourOf = (bucket) => Number(bucket.slice(11, 13));

export const formatHour = (bucket) => `${localHourOf(bucket)}h`;

export const formatBucket = (bucket, granularity, language) => (granularity === "hour" ? formatHour(bucket) : formatDay(bucket, language));
